import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  X, Inbox, Check, CheckCheck, MoreHorizontal,
  Trash2, Clock, ChevronDown, PartyPopper,
  Send, Loader,
} from 'lucide-react';
import type { Todo, TodoUpdate, TaskTriageSuggestion } from '../types';
import { TaskStatus } from '../types';
import { getInboxTasks, isTaskProcessed } from '../utils/inboxFilter';
import { aiAPI } from '../services/api';
import { useTaskCapture } from '../hooks/useTaskCapture';
import { useWindows } from '../contexts/WindowsContext';

interface ProcessInboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  allTodos: Todo[];
  onUpdateTodo: (id: string, data: Partial<TodoUpdate>) => void;
  onDeleteTodo: (id: string) => void;
  onTodoClick: (todo: Todo) => void;
}

const TIME_PRESETS = [
  { label: '15m', value: '15' },
  { label: '30m', value: '30' },
  { label: '1h', value: '60' },
  { label: '2h', value: '120' },
  { label: '4h', value: '240' },
];

// ─── Capture Bar ──────────────────────────────────────────

function CaptureBar() {
  const capture = useTaskCapture('process-inbox');

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3">
      <div className="flex items-center gap-2">
        <span className="text-gray-400 text-sm font-medium">+</span>
        <input
          type="text"
          value={capture.input}
          onChange={e => capture.setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void capture.submit(); }}
          placeholder="Quick add: buy groceries, call dentist, write report..."
          className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400"
          disabled={capture.isLoading}
        />
        {capture.input.trim() && (
          <button
            onClick={() => void capture.submit()}
            disabled={capture.isLoading}
            className="p-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {capture.isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        )}
      </div>
      {capture.progress && <p role="status" className="text-xs text-gray-500 mt-1">{capture.progress}</p>}
      {capture.receipt && (
        <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
          <Check className="w-3 h-3" /> {capture.receipt}
        </p>
      )}
      {capture.reply && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{capture.reply}</p>}
      {capture.error && <p role="alert" className="text-xs text-red-600 dark:text-red-400 mt-1">{capture.error}</p>}
    </div>
  );
}

// ─── Row Overflow Menu ────────────────────────────────────

interface OverflowMenuProps {
  onComplete: () => void;
  onSomeday: () => void;
  onDelete: () => void;
  onOpenFull: () => void;
}

function OverflowMenu({ onComplete, onSomeday, onDelete, onOpenFull }: OverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 py-1 z-50 min-w-[140px]">
          <button onClick={() => { onOpenFull(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Open Full Form
          </button>
          <button onClick={() => { onComplete(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm text-green-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            Complete
          </button>
          <button onClick={() => { onSomeday(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm text-yellow-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            Someday
          </button>
          <hr className="my-1 border-gray-200 dark:border-gray-700" />
          <button onClick={() => { onDelete(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            <Trash2 className="w-3 h-3 inline mr-1" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  [TaskStatus.NOT_STARTED]: { label: 'New', color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
  [TaskStatus.IN_PROGRESS]: { label: 'Active', color: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' },
  [TaskStatus.WAITING]: { label: 'Wait', color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' },
};

interface StatusBadgeProps {
  value: TaskStatus;
  onChange: (v: TaskStatus) => void;
}

function StatusBadge({ value, onChange }: StatusBadgeProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const config = STATUS_CONFIG[value] || STATUS_CONFIG[TaskStatus.NOT_STARTED];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${config.color}`}
      >
        {config.label}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 py-1 z-50 min-w-[110px]">
          {Object.entries(STATUS_CONFIG).map(([status, cfg]) => (
            <button
              key={status}
              onClick={() => { onChange(status as TaskStatus); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 ${
                value === status ? 'font-medium' : ''
              } text-gray-700 dark:text-gray-300`}
            >
              {cfg.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Time Picker ──────────────────────────────────────────

interface TimePickerProps {
  value: string | undefined;
  suggestion?: string | null;
  onChange: (val: string | undefined) => void;
}

function TimePicker({ value, suggestion, onChange }: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const displayValue = value
    ? TIME_PRESETS.find(p => p.value === value)?.label || `${value}m`
    : null;

  const ghostText = !value && suggestion
    ? TIME_PRESETS.find(p => p.value === suggestion)?.label || `${suggestion}m`
    : null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`text-xs px-2 py-1 rounded border transition-colors w-full text-left ${
          value
            ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-400'
        }`}
      >
        {displayValue || (ghostText ? (
          <span className="italic text-gray-400 cursor-pointer" onClick={e => {
            e.stopPropagation();
            if (suggestion) onChange(suggestion);
          }}>{ghostText}</span>
        ) : (
          <Clock className="w-3 h-3 inline text-gray-400" />
        ))}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
          {TIME_PRESETS.map(p => (
            <button
              key={p.value}
              onClick={() => { onChange(p.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 ${
                p.value === value ? 'text-blue-600 font-medium' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              {p.label}
            </button>
          ))}
          {value && (
            <>
              <hr className="my-1 border-gray-200 dark:border-gray-700" />
              <button onClick={() => { onChange(undefined); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700">Clear</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inbox Task Row ───────────────────────────────────────

interface InboxTaskRowProps {
  todo: Todo;
  suggestion?: TaskTriageSuggestion;
  onUpdate: (id: string, data: Partial<TodoUpdate>) => void;
  onDelete: (id: string) => void;
  onClick: (todo: Todo) => void;
  isGraduating: boolean;
}

function InboxTaskRow({ todo, suggestion, onUpdate, onDelete, onClick, isGraduating }: InboxTaskRowProps) {
  const [nextStepValue, setNextStepValue] = useState(todo.next_step || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextStepInputRef = useRef<HTMLInputElement>(null);

  const handleNextStepChange = (val: string) => {
    setNextStepValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onUpdate(todo.id, { next_step: val });
    }, 300);
  };

  const handleAcceptAll = () => {
    if (!suggestion) return;
    const updates: Partial<TodoUpdate> = {};
    if (suggestion.suggested_next_step && !todo.next_step) {
      updates.next_step = suggestion.suggested_next_step;
      setNextStepValue(suggestion.suggested_next_step);
    }
    if (suggestion.suggested_estimated_time && !todo.estimated_time_minutes) {
      updates.estimated_time_minutes = suggestion.suggested_estimated_time;
    }
    if (Object.keys(updates).length > 0) {
      onUpdate(todo.id, updates);
    }
  };

  const hasSuggestions = suggestion && (
    (suggestion.suggested_next_step && !todo.next_step) ||
    (suggestion.suggested_estimated_time && !todo.estimated_time_minutes)
  );

  const processed = isTaskProcessed(todo);

  const nextStepSuggestion = suggestion?.suggested_next_step && !todo.next_step && !nextStepValue
    ? suggestion.suggested_next_step
    : null;

  return (
    <tr className={`border-b border-gray-100 dark:border-gray-700 transition-all duration-500 ${
      isGraduating ? 'opacity-0 bg-green-50 dark:bg-green-900/20' :
      processed ? 'bg-green-50/50 dark:bg-green-900/10' : ''
    }`}>
      {/* Task title */}
      <td className="py-2 px-3" style={{ maxWidth: 300 }}>
        <div className="flex items-center gap-2">
          {processed && <div className="w-0.5 h-6 bg-green-400 rounded-full flex-shrink-0" />}
          <button
            onClick={() => onClick(todo)}
            className="text-sm text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 text-left truncate"
            title={todo.title}
          >
            {todo.title || <span className="italic text-gray-400">Untitled</span>}
          </button>
          {todo.deadline && new Date(todo.deadline) < new Date() && (
            <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full flex-shrink-0">overdue</span>
          )}
        </div>
      </td>
      {/* Next Step */}
      <td className="py-2 px-2">
        <div className="relative" style={{ minWidth: 180 }}>
          <input
            ref={nextStepInputRef}
            type="text"
            value={nextStepValue}
            onChange={e => handleNextStepChange(e.target.value)}
            placeholder="Next step..."
            className="text-xs w-full px-2 py-1 rounded border border-dashed border-gray-300 dark:border-gray-600 bg-transparent outline-none focus:border-blue-400 transition-colors"
          />
          {nextStepSuggestion && (
            <button
              onClick={() => {
                const val = suggestion!.suggested_next_step!;
                setNextStepValue(val);
                onUpdate(todo.id, { next_step: val });
                setTimeout(() => {
                  if (nextStepInputRef.current) {
                    nextStepInputRef.current.focus();
                    nextStepInputRef.current.select();
                  }
                }, 0);
              }}
              className="absolute inset-0 px-2 py-1 text-xs text-gray-400 italic text-left truncate cursor-pointer hover:text-gray-500 rounded"
            >
              {nextStepSuggestion}
            </button>
          )}
        </div>
      </td>
      {/* Est. Time */}
      <td className="py-2 px-2 w-[60px]">
        <TimePicker
          value={todo.estimated_time_minutes}
          suggestion={suggestion?.suggested_estimated_time}
          onChange={val => onUpdate(todo.id, { estimated_time_minutes: val })}
        />
      </td>
      {/* Status */}
      <td className="py-2 px-2 w-[60px] hidden lg:table-cell">
        <StatusBadge
          value={todo.status}
          onChange={status => onUpdate(todo.id, { status })}
        />
      </td>
      {/* Actions */}
      <td className="py-2 px-2 w-[60px]">
        <div className="flex items-center gap-1">
          {hasSuggestions && (
            <button
              onClick={handleAcceptAll}
              className="p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-500 hover:text-blue-600"
              title="Accept all suggestions"
            >
              <CheckCheck className="w-3.5 h-3.5" />
            </button>
          )}
          <OverflowMenu
            onComplete={() => onUpdate(todo.id, { status: TaskStatus.COMPLETED })}
            onSomeday={() => onUpdate(todo.id, { horizon: 'someday', status: TaskStatus.NOT_STARTED })}
            onDelete={() => onDelete(todo.id)}
            onOpenFull={() => onClick(todo)}
          />
        </div>
      </td>
    </tr>
  );
}

// ─── Mobile Inbox Card ────────────────────────────────────

function MobileInboxCard({ todo, suggestion, onUpdate, onDelete, onClick, isGraduating }: InboxTaskRowProps) {
  const [nextStepValue, setNextStepValue] = useState(todo.next_step || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleNextStepChange = (val: string) => {
    setNextStepValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onUpdate(todo.id, { next_step: val });
    }, 300);
  };

  return (
    <div className={`p-3 border-b border-gray-100 dark:border-gray-700 transition-all duration-500 ${
      isGraduating ? 'opacity-0 bg-green-50 dark:bg-green-900/20' :
      isTaskProcessed(todo) ? 'bg-green-50/50 dark:bg-green-900/10' : ''
    }`}>
      {/* Title */}
      <button
        onClick={() => onClick(todo)}
        className="text-sm font-medium text-gray-800 dark:text-gray-200 hover:text-blue-600 text-left w-full truncate mb-2"
      >
        {todo.title || <span className="italic text-gray-400">Untitled</span>}
      </button>

      {/* Next step */}
      <input
        type="text"
        value={nextStepValue}
        onChange={e => handleNextStepChange(e.target.value)}
        placeholder={suggestion?.suggested_next_step && !nextStepValue ? suggestion.suggested_next_step : "Next step..."}
        className="text-xs w-full px-2 py-1.5 rounded border border-dashed border-gray-300 dark:border-gray-600 bg-transparent outline-none focus:border-blue-400 mb-2"
      />

      {/* Time row */}
      <div className="flex items-center gap-2">
        <div className="w-16">
          <TimePicker
            value={todo.estimated_time_minutes}
            suggestion={suggestion?.suggested_estimated_time}
            onChange={val => onUpdate(todo.id, { estimated_time_minutes: val })}
          />
        </div>
        <OverflowMenu
          onComplete={() => onUpdate(todo.id, { status: TaskStatus.COMPLETED })}
          onSomeday={() => onUpdate(todo.id, { horizon: 'someday', status: TaskStatus.NOT_STARTED })}
          onDelete={() => onDelete(todo.id)}
          onOpenFull={() => onClick(todo)}
        />
      </div>
    </div>
  );
}

// ─── Inbox Empty State ────────────────────────────────────

function InboxEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
        <PartyPopper className="w-8 h-8 text-green-600 dark:text-green-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Inbox Zero!</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
        All tasks have been processed. Use the capture bar above to add new tasks.
      </p>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────

export function ProcessInboxModal({
  isOpen,
  onClose,
  allTodos,
  onUpdateTodo,
  onDeleteTodo,
  onTodoClick,
}: ProcessInboxModalProps) {
  const { isMobile } = useWindows();
  const inboxTasks = useMemo(() => getInboxTasks(allTodos), [allTodos]);
  const [graduatingIds, setGraduatingIds] = useState<Set<string>>(new Set());
  const [suggestions, setSuggestions] = useState<Map<string, TaskTriageSuggestion>>(new Map());
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const prevInboxIdsRef = useRef<Set<string>>(new Set());
  const [headerHeight, setHeaderHeight] = useState(0);

  // Measure header height when modal opens
  useEffect(() => {
    if (isOpen) {
      const header = document.querySelector('header');
      if (header) {
        setHeaderHeight(header.getBoundingClientRect().height);
      }
    }
  }, [isOpen]);

  // Track processed tasks via auto-graduation animation
  const processedCount = useMemo(() => {
    return allTodos.filter(t => {
      return prevInboxIdsRef.current.has(t.id) && isTaskProcessed(t);
    }).length;
  }, [allTodos]);

  // Detect tasks that just became processed and animate them out
  useEffect(() => {
    if (!isOpen) return;
    const currentInboxIds = new Set(inboxTasks.map(t => t.id));

    const graduated = new Set<string>();
    prevInboxIdsRef.current.forEach(id => {
      if (!currentInboxIds.has(id)) {
        graduated.add(id);
      }
    });

    if (graduated.size > 0) {
      setGraduatingIds(graduated);
      setTimeout(() => {
        setGraduatingIds(new Set());
      }, 500);
    }

    prevInboxIdsRef.current = currentInboxIds;
  }, [inboxTasks, isOpen]);

  // Fetch AI suggestions
  const fetchSuggestions = useCallback(async (taskIds: string[]) => {
    if (taskIds.length === 0) return;
    setIsLoadingSuggestions(true);
    try {
      const response = await aiAPI.getTriageSuggestions(taskIds);
      const newSuggestions = new Map(suggestions);
      for (const s of response.suggestions) {
        newSuggestions.set(s.task_id, s);
      }
      setSuggestions(newSuggestions);
    } catch {
      // Silently fail — grid remains usable for manual triage
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [suggestions]);

  // Fetch suggestions when modal opens
  useEffect(() => {
    if (isOpen && inboxTasks.length > 0) {
      const idsToFetch = inboxTasks
        .filter(t => !suggestions.has(t.id))
        .map(t => t.id)
        .slice(0, 20);
      if (idsToFetch.length > 0) {
        fetchSuggestions(idsToFetch);
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle task update with wrapping
  const handleUpdate = useCallback((id: string, data: Partial<TodoUpdate>) => {
    onUpdateTodo(id, data);
  }, [onUpdateTodo]);

  const handleTodoClick = useCallback((todo: Todo) => {
    onTodoClick(todo);
  }, [onTodoClick]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGraduatingIds(new Set());
      prevInboxIdsRef.current = new Set();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const totalTasks = inboxTasks.length + processedCount;
  const progressPct = totalTasks > 0 ? Math.round((processedCount / totalTasks) * 100) : 100;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-gray-900 flex flex-col overflow-hidden shadow-lg"
      style={{ top: headerHeight }}
    >
      {/* Slim sub-header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Process Inbox{inboxTasks.length > 0 && ` (${inboxTasks.length} tasks)`}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {totalTasks > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400">{processedCount}/{totalTasks}</span>
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Capture Bar */}
      <CaptureBar />

      {/* Content — cards on mobile, table on desktop */}
      <div className="flex-1 overflow-y-auto">
        {inboxTasks.length === 0 ? (
          <InboxEmptyState />
        ) : isMobile ? (
          <div>
            {inboxTasks.map(todo => (
              <MobileInboxCard
                key={todo.id}
                todo={todo}
                suggestion={suggestions.get(todo.id)}
                onUpdate={handleUpdate}
                onDelete={onDeleteTodo}
                onClick={handleTodoClick}
                isGraduating={graduatingIds.has(todo.id)}
              />
            ))}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
              <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <th className="text-left py-2 px-3 font-medium" style={{ maxWidth: 300 }}>Task</th>
                <th className="text-left py-2 px-2 font-medium">Next Step</th>
                <th className="text-left py-2 px-2 font-medium w-[60px]">Time</th>
                <th className="text-left py-2 px-2 font-medium w-[60px] hidden lg:table-cell">Status</th>
                <th className="py-2 px-2 w-[60px]"></th>
              </tr>
            </thead>
            <tbody>
              {inboxTasks.map(todo => (
                <InboxTaskRow
                  key={todo.id}
                  todo={todo}
                  suggestion={suggestions.get(todo.id)}
                  onUpdate={handleUpdate}
                  onDelete={onDeleteTodo}
                  onClick={handleTodoClick}
                  isGraduating={graduatingIds.has(todo.id)}
                />
              ))}
            </tbody>
          </table>
        )}
        {isLoadingSuggestions && inboxTasks.length > 0 && (
          <div className="flex items-center justify-center py-3 text-xs text-gray-400">
            <Loader className="w-3 h-3 animate-spin mr-1" /> Loading AI suggestions...
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              inboxTasks.forEach(t => {
                if (!isTaskProcessed(t)) {
                  onUpdateTodo(t.id, { horizon: 'someday', status: TaskStatus.NOT_STARTED });
                }
              });
            }}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            disabled={inboxTasks.length === 0}
          >
            <ChevronDown className="w-4 h-4 inline mr-1" />
            Send all to Someday
          </button>
          <div className="flex items-center gap-3">
            {suggestions.size > 0 && (
              <button
                onClick={() => fetchSuggestions(inboxTasks.map(t => t.id).slice(0, 20))}
                className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
                disabled={isLoadingSuggestions}
              >
                Refresh Suggestions
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessInboxModal;
